import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const primaryNav = [
  { label: "首页", to: "/" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  const isCategoryActive = location.pathname.startsWith("/category");

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
    setKeyword("");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-800/80 bg-background-950/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex shrink-0 items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 font-heading text-lg font-bold text-background-50">
              亚
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-[15px] font-bold tracking-wide text-foreground-50 md:text-base">
                亚洲激情视频
              </span>
              <span className="mt-1 text-[9px] uppercase tracking-[0.3em] text-foreground-500">
                Asia Passion
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            <Link
              to="/"
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                location.pathname === "/"
                  ? "bg-primary-600/20 text-primary-300"
                  : "text-foreground-300 hover:bg-background-800 hover:text-foreground-50"
              }`}
            >
              首页
            </Link>

            <div className="relative">
              <button
                type="button"
                onClick={() => setCategoryOpen((prev) => !prev)}
                aria-expanded={categoryOpen}
                className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  isCategoryActive
                    ? "bg-primary-600/20 text-primary-300"
                    : "text-foreground-300 hover:bg-background-800 hover:text-foreground-50"
                }`}
              >
                分类
                <span className="flex h-4 w-4 items-center justify-center">
                  <i
                    className={`ri-arrow-down-s-line text-sm transition-transform ${
                      categoryOpen ? "rotate-180" : ""
                    }`}
                  />
                </span>
              </button>

              {categoryOpen && (
                <div className="absolute left-0 top-full mt-2 w-[340px] animate-fade-in rounded-lg border border-background-700 bg-background-900 p-2">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((category) => (
                      <Link
                        key={category.slug}
                        to={`/category/${category.slug}`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-300 transition-colors hover:bg-background-800 hover:text-primary-200"
                      >
                        <span className="flex h-6 w-6 items-center justify-center rounded bg-background-800">
                          <i className={`${category.icon} text-xs text-primary-300`} />
                        </span>
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {primaryNav.slice(1).map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    active
                      ? "bg-primary-600/20 text-primary-300"
                      : "text-foreground-300 hover:bg-background-800 hover:text-foreground-50"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="hidden lg:block">
              <div className="flex h-9 w-[220px] items-center gap-2 rounded-full border border-background-700 bg-background-900 px-3 transition-colors focus-within:border-primary-600">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-search-line text-sm text-foreground-500" />
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索视频或标签"
                  aria-label="搜索视频"
                  className="h-full w-full border-0 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none"
                />
              </div>
            </form>

            <span className="hidden items-center gap-1 rounded-full border border-primary-700/70 bg-primary-950/60 px-2.5 py-1 text-[11px] font-medium text-primary-200 sm:flex">
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-shield-check-line text-[11px]" />
              </span>
              18+
            </span>

            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-700 text-foreground-200 transition-colors hover:border-primary-600 hover:text-primary-300 md:hidden"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-lg`} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="animate-fade-in border-t border-background-800 bg-background-950 px-4 pb-5 pt-4 md:hidden">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex h-10 w-full items-center gap-2 rounded-md border border-background-700 bg-background-900 px-3">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-search-line text-sm text-foreground-500" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索视频或标签"
                aria-label="搜索视频"
                className="h-full w-full border-0 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none"
              />
            </div>
          </form>

          <div className="flex flex-col gap-1">
            <Link to="/" className="rounded-md px-3 py-2.5 text-sm text-foreground-200 hover:bg-background-800">
              首页
            </Link>
            <p className="px-3 pb-1 pt-3 text-[11px] uppercase tracking-[0.2em] text-foreground-600">
              内容分类
            </p>
            {categories.map((category) => (
              <Link
                key={category.slug}
                to={`/category/${category.slug}`}
                className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-200 hover:bg-background-800"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className={`${category.icon} text-sm text-primary-300`} />
                </span>
                {category.name}
              </Link>
            ))}
            <div className="my-2 h-px bg-background-800" />
            <Link to="/search" className="rounded-md px-3 py-2.5 text-sm text-foreground-200 hover:bg-background-800">
              搜索
            </Link>
            <Link to="/about" className="rounded-md px-3 py-2.5 text-sm text-foreground-200 hover:bg-background-800">
              关于与联系
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}